/**
 * Funding Plate front end, entry point.
 *
 * Two states: signed out, which is the access page, and signed in, which is the
 * shell around the resolver. The bootstrap call decides which, and a 401 on any
 * later call returns here rather than leaving a half-painted surface, because a
 * session that expired mid-navigation should land on the sign-in page and not on
 * an empty table.
 */
import { StrictMode, useEffect } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter, HashRouter, Route, Routes } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'

import { Resolver } from './app/Resolver'
import { Shell } from './app/Shell'
import { MOUNT } from './app/paths'
import { IS_STATIC } from './api/client'
import { SessionProvider, applyTheme, useBootstrapQuery } from './app/session'
import { AuditProvider } from './components/Audit'
import { ExplainProvider } from './components/Explainer'
import { Note, Spinner } from './components/bits'
import { Access } from './screens/Access'
import './styles/trimeda.css'
import './styles/react.css'

const client = new QueryClient({
  defaultOptions: {
    queries: {
      // A surface is read fresh when it is opened. Caching a list for minutes
      // would show a decision somebody else has already taken as still
      // pending, which on this platform is worse than a second request.
      staleTime: 0,
      refetchOnWindowFocus: false,
      retry: false,
    },
  },
})

function SignedIn() {
  const { data: boot, error, isLoading } = useBootstrapQuery()

  useEffect(() => { if (boot) applyTheme(boot) }, [boot])

  if (isLoading) return <Spinner what="your session" />
  // A session that expired and a platform that is down both land here. The
  // access page tells them apart: it probes readiness and names the fault.
  if (error || !boot) return <Access />

  return (
    <SessionProvider boot={boot}>
      <ExplainProvider>
        <AuditProvider>
          <Shell>
            <Routes>
              <Route path="/app/*" element={<Resolver />} />
              <Route path="*" element={<Resolver />} />
            </Routes>
          </Shell>
        </AuditProvider>
      </ExplainProvider>
    </SessionProvider>
  )
}

function App() {
  return (
    <Routes>
      <Route path="/" element={<Access />} />
      <Route path="/app/*" element={<SignedIn />} />
      <Route path="*" element={<SignedIn />} />
    </Routes>
  )
}

const root = document.getElementById('root')
if (!root) {
  document.body.innerHTML = '<p>No mount point.</p>'
} else {
  createRoot(root).render(
    <StrictMode>
      <QueryClientProvider client={client}>
        {/* The static build routes on the hash so a deep link survives a
            reload on a server that knows nothing about this application, and
            so the folder works from a subdirectory. */}
        {IS_STATIC
          ? <HashRouter><ErrorBoundaryless /></HashRouter>
          : <BrowserRouter basename={MOUNT}><ErrorBoundaryless /></BrowserRouter>}
      </QueryClientProvider>
    </StrictMode>,
  )
}

/**
 * A thin wrapper that reports a failed bootstrap in words.
 *
 * Not a React error boundary: a boundary catches a render fault, and the
 * failure worth explaining here is a server that answered badly. A blank page
 * with a console message is the one outcome nobody can act on.
 */
function ErrorBoundaryless() {
  return (
    <>
      <noscript>
        <Note kind="bad">
          This front end needs JavaScript. The server rendered tier at / does
          not, and serves the same platform.
        </Note>
      </noscript>
      <App />
    </>
  )
}
